import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { format, isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import {
  CalendarDays,
  Clock,
  Flag,
  Phone,
  Clock1,
  Clock2,
  Clock3,
  Clock4,
  Clock5,
  Clock6,
  Clock7,
  Clock8,
  Clock9,
  Clock10,
  Clock11,
  Clock12,
} from "lucide-react";
import Card from "../../../components/ui/Card";
import ActivityAction from "../../../actions/ActivityAction";
import { getBaseUrl } from "../../../api/api";

const ScheduleCard = ({
  item,
  activity,
  onClickItem,
}: {
  item: any;
  activity: any;
  onClickItem: () => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);

  const contentRef = useRef(null);

  const type = activity.getIn(["event", "type"]);
  const title = activity.getIn(["event", "data", "summary"]) || t("Schedule activity");
  const description = activity.getIn(["event", "data", "description"]) || "";

  const timeZone = activity.getIn(["event", "data", "timeZone"]);
  let start = activity.getIn(["event", "data", "start"]);
  if (typeof start === "number") {
    start = formatInTimeZone(
      start,
      timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      "yyyy-MM-dd"
    );
  }
  const startTime = activity.getIn(["event", "data", "startTime"]);
  let end = activity.getIn(["event", "data", "end"]);
  if (typeof end === "number") {
    end = formatInTimeZone(
      end,
      timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      "yyyy-MM-dd"
    );
  }
  const endTime = activity.getIn(["event", "data", "endTime"]);

  const startDateTime = new Date(`${start}T${startTime}`);
  const endDateTime = new Date(`${end}T${endTime}`);
  const startDateTimeText = isValid(startDateTime) ? format(startDateTime, "hh:mm aa") : "";
  const endDateTimeText = isValid(endDateTime) ? format(endDateTime, "hh:mm aa") : "";

  useEffect(() => {
    if (contentRef.current) {
      const lineHeight = parseFloat(window.getComputedStyle(contentRef.current).lineHeight);
      const maxHeight = lineHeight * 3;
      setShowToggle(contentRef.current.scrollHeight > maxHeight);
    }
  }, [description]);

  const renderIconTitle = () => {
    if (activity.getIn(["event", "data", "hasGmail"])) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/google.png`}
          alt="Google"
        />
      );
    }
    if (activity.getIn(["event", "data", "hasOutlook"])) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/microsoft.png`}
          alt="Microsoft"
        />
      );
    }
    return null;
  };

  const renderClockByTime = () => {
    switch (startDateTime.getHours()) {
      case 1:
        return <Clock1 className="h-4 w-4" aria-hidden="true" />;
      case 2:
        return <Clock2 className="h-4 w-4" aria-hidden="true" />;
      case 3:
        return <Clock3 className="h-4 w-4" aria-hidden="true" />;
      case 4:
        return <Clock4 className="h-4 w-4" aria-hidden="true" />;
      case 5:
        return <Clock5 className="h-4 w-4" aria-hidden="true" />;
      case 6:
        return <Clock6 className="h-4 w-4" aria-hidden="true" />;
      case 7:
        return <Clock7 className="h-4 w-4" aria-hidden="true" />;
      case 8:
        return <Clock8 className="h-4 w-4" aria-hidden="true" />;
      case 9:
        return <Clock9 className="h-4 w-4" aria-hidden="true" />;
      case 10:
        return <Clock10 className="h-4 w-4" aria-hidden="true" />;
      case 11:
        return <Clock11 className="h-4 w-4" aria-hidden="true" />;
      case 12:
        return <Clock12 className="h-4 w-4" aria-hidden="true" />;
      default:
        return <Clock className="h-4 w-4" aria-hidden="true" />;
    }
  };

  return (
    <Card className="overflow-hidden w-full h-full">
      <div className="p-2 w-full space-y-2">
        <div className="w-full space-y-1">
          <div
            className={`w-full pt-1 flex items-center gap-2 text-gray-500 group-hover:text-gray-700`}
          >
            {renderClockByTime()}
            <div className="font-medium">
              {type === "deadline"
                ? startDateTimeText
                : `${startDateTimeText} - ${endDateTimeText}`}
            </div>
          </div>
          <div className="w-full flex items-center space-x-1">
            {renderIconTitle()}
            <div
              title={title}
              className={`w-full font-semibold text-gray-700 truncate cursor-pointer`}
              onClick={() => {
                dispatch(
                  ActivityAction.showActivityDialog(activity.get("itemId"), activity.get("id"))
                );
              }}
            >
              {title}
            </div>
          </div>
          <div className="w-full inline-flex items-center space-x-1">
            <div
              className={`flex items-center rounded-lg text-gray-700 border border-gray-300 px-1 py-0.5 space-x-1`}
            >
              {type === "meeting" && <CalendarDays className="h-3 w-3" aria-hidden="true" />}
              {type === "call" && <Phone className="h-3 w-3" aria-hidden="true" />}
              {type === "deadline" && <Flag className="h-3 w-3" aria-hidden="true" />}
              <div className="text-xs capitalize">{type}</div>
            </div>
            <div
              title={item.getIn(["data", "name", "value"])}
              className={`text-xs font-semibold text-gray-500 truncate cursor-pointer underline`}
              onClick={onClickItem}
            >
              {item.getIn(["data", "name", "value"])}
            </div>
          </div>
        </div>
        <div>
          <div
            ref={contentRef}
            className={`
              wrap-anywhere
              transition-all
              duration-300
              overflow-hidden
              ${expanded ? "" : "line-clamp-3"}
            `}
            dangerouslySetInnerHTML={{ __html: description }}
          />
          {showToggle && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800 transition cursor-pointer"
            >
              {expanded ? t("Show less") : t("Show more")}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ScheduleCard;
